// reviews-carousel — swiper lifecycle for the Reviews section. Stimulus owns
// connect/disconnect, so a carousel re-rendered in place (the builder preview
// swaps section markup on every edit) re-initializes itself.
import { Controller } from '@hotwired/stimulus'
import { loadSwiper } from 'utils/loadSwiper'

// simulateTouch — mouse dragging: off on desktop (arrows page the carousel,
// and a drag-start would swallow the builder's slot clicks), on below 1024px.
// Real touch swiping works regardless.
const carouselOptions = (element, mouseDrag) => ({
  spaceBetween: 24,
  slidesPerView: 1.1,
  simulateTouch: mouseDrag,
  watchSlidesProgress: true,
  navigation: {
    nextEl: element.querySelector('.reviews-carousel__control_next'),
    prevEl: element.querySelector('.reviews-carousel__control_prev')
  },
  breakpoints: {
    391: {
      slidesPerView: 1.2
    },
    769: {
      slidesPerView: 2.3,
      spaceBetween: 32
    },
    1441: {
      slidesPerView: 3.3
    }
  }
})

// Below the desktop tier the slides keep their fixed width (see the
// .swiper-slide width in reviews.sass) and the screen fits what it fits.
const defaultOptions = (element, mouseDrag) => ({
  spaceBetween: 24,
  slidesPerView: 'auto',
  simulateTouch: mouseDrag,
  navigation: {
    nextEl: element.querySelector('.reviews-carousel__control_next'),
    prevEl: element.querySelector('.reviews-carousel__control_prev')
  },
  breakpoints: {
    769: {
      slidesPerView: 'auto',
      spaceBetween: 32
    },
    1024: {
      slidesPerView: 3,
      spaceBetween: 32
    }
  }
})

// data-loop on the swiper root turns the carousel infinite: the arrows wrap
// around instead of disabling at the ends. While a section is being EDITED
// (the engine flips data-editing-active on the body) the loop turns off —
// swiper's cloned slides aren't bound to edit slots, so clicks on them
// would be dead.
const editingActive = () => document.body.dataset.editingActive === 'true'

// Loop only when there is something to page on the widest tier (3 visible):
// with 3 slides swiper locks the arrows and they hide. Editing never loops —
// swiper's cloned slides aren't bound to edit slots, clicks would be dead.
const withLoop = (element, options) => {
  const slides = element.querySelectorAll('.swiper-slide').length
  return element.dataset.loop && !editingActive() && slides > 3
    ? { ...options, loop: true }
    : options
}

export default class extends Controller {
  connect() {
    this.section = this.element.closest('.reviews')
    this.swiper = null
    this.started = false

    // Crossing the desktop boundary swaps the event set (mouse drag on/off),
    // which swiper only reads at init — re-create the carousel.
    this.desktopQuery = window.matchMedia('(min-width: 1024px)')
    this.onTierChange = () => {
      if (!this.started || this.section.classList.contains('reviews--static'))
        return
      if (this.swiper) this.swiper.destroy(true, true)
      this.swiper = null
      this.init()
    }
    this.desktopQuery.addEventListener('change', this.onTierChange)

    // Edit mode arms AFTER the carousel inits — rebuild on the flag flip so
    // the loop clones appear/disappear with it.
    this.editObserver = new MutationObserver(this.onTierChange)
    this.editObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-editing-active']
    })

    this.observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return
          observer.unobserve(entry.target)
          this.init()
        })
      },
      { rootMargin: '200px' }
    )
    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer.disconnect()
    this.editObserver.disconnect()
    this.desktopQuery.removeEventListener('change', this.onTierChange)
    if (this.mediaQuery)
      this.mediaQuery.removeEventListener('change', this.sync)
    if (this.swiper) this.swiper.destroy(true, true)
    this.swiper = null
  }

  init() {
    this.started = true
    if (this.section.classList.contains('reviews--static')) {
      this.initStatic()
      return
    }

    const mouseDrag = !this.desktopQuery.matches
    const options = withLoop(
      this.element,
      this.section.classList.contains('reviews--carousel')
        ? carouselOptions(this.element, mouseDrag)
        : defaultOptions(this.element, mouseDrag)
    )
    loadSwiper().then(({ Swiper, Navigation }) => {
      if (!this.element.isConnected) return
      Swiper.use([Navigation])
      this.swiper = new Swiper(this.element, options)
    })
  }

  // Static mode runs the slider below the desktop tier only.
  initStatic() {
    this.mediaQuery = window.matchMedia('(max-width: 1023px)')
    this.sync = async () => {
      if (!this.element.isConnected) return

      if (this.mediaQuery.matches && !this.swiper) {
        const { Swiper } = await loadSwiper()
        if (!this.element.isConnected || this.swiper) return

        this.swiper = new Swiper(this.element, {
          spaceBetween: 24,
          slidesPerView: 'auto',
          breakpoints: {
            769: {
              slidesPerView: 'auto',
              spaceBetween: 32
            }
          }
        })
      }

      if (!this.mediaQuery.matches && this.swiper) {
        this.swiper.destroy(true, true)
        this.swiper = null
      }
    }

    this.sync()
    this.mediaQuery.addEventListener('change', this.sync)
  }
}
